import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Plus, Edit, Trash2, Package, Calendar, Users, CreditCard, ToggleLeft, ToggleRight, Upload, Clock, ExternalLink, CheckCircle, AlertCircle, Link2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { compressImage } from '../../utils/imageUtils';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../../config/firebase';
import { useBusiness } from '../../contexts/BusinessContext';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Loading } from '../../components/ui/Loading';

// Tipos de restricción horaria
type TimeRestriction = 'all_day' | 'morning' | 'afternoon';
// Tipos de restricción de clases
type ClassRestriction = 'all' | 'specific';

// Tipo para membresías mensuales (se renuevan cada día 1)
interface Membership {
  id: string;
  name: string;
  price: number;
  reservationsPerMonth: number;
  imageUrl?: string;
  isActive: boolean;
  businessId: string;
  timeRestriction: TimeRestriction; // Restricción horaria
  classRestriction: ClassRestriction; // Restricción de clases
  allowedClassTypeIds?: string[]; // IDs de clases permitidas (cuando classRestriction = 'specific')
  weeklyLimitEnabled?: boolean;
  weeklyClassCount?: number;
  createdAt: Date;
  updatedAt: Date;
}

// Tipo para bonos (duración fija desde contratación, NO se renuevan)
interface PassType {
  id: string;
  passName: string;
  classCredits: number;
  validityInDays: number;
  price?: number;
  imageUrl?: string;
  isActive: boolean;
  businessId: string;
  timeRestriction: TimeRestriction; // Restricción horaria
  classRestriction: ClassRestriction; // Restricción de clases
  allowedClassTypeIds?: string[]; // IDs de clases permitidas (cuando classRestriction = 'specific')
  createdAt: Date;
  updatedAt: Date;
}

// Tipo para los tipos de clase (para selección)
interface ClassType {
  id: string;
  name: string;
  isActive: boolean;
}

interface MembershipFormData {
  name: string;
  price: number;
  reservationsPerMonth: number;
  imageUrl: string;
  isActive: boolean;
  timeRestriction: TimeRestriction;
  classRestriction: ClassRestriction;
  allowedClassTypeIds: string[];
  weeklyLimitEnabled: boolean;
  weeklyClassCount: number;
}

interface PassFormData {
  passName: string;
  classCredits: number;
  validityInDays: number;
  price: number;
  imageUrl: string;
  isActive: boolean;
  timeRestriction: TimeRestriction;
  classRestriction: ClassRestriction;
  allowedClassTypeIds: string[];
}

// Estado de Stripe Connect
interface StripeConnectStatus {
  connected: boolean;
  accountId?: string;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  status: 'not_created' | 'pending' | 'active';
}

const ClassPassManagementPage: React.FC = () => {
  const { businessId } = useBusiness();
  const [passTypes, setPassTypes] = useState<PassType[]>([]);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [loading, setLoading] = useState(true);

  // Configuración de modo de pago
  const [paymentMode, setPaymentMode] = useState<'online' | 'in_person'>('in_person');
  const [savingPaymentMode, setSavingPaymentMode] = useState(false);

  // Estado de Stripe Connect
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus>({ connected: false, status: 'not_created' });
  const [loadingStripe, setLoadingStripe] = useState(false);

  // Estados para formulario de bonos
  const [showPassForm, setShowPassForm] = useState(false);
  const [editingPass, setEditingPass] = useState<PassType | null>(null);
  const [passFormData, setPassFormData] = useState<PassFormData>({
    passName: '',
    classCredits: 1,
    validityInDays: 30,
    price: 0,
    imageUrl: '',
    isActive: true,
    timeRestriction: 'all_day',
    classRestriction: 'all',
    allowedClassTypeIds: []
  });
  const [uploadingPassImage, setUploadingPassImage] = useState(false);
  const [passImagePreview, setPassImagePreview] = useState<string | null>(null);
  const passFileInputRef = useRef<HTMLInputElement>(null);

  // Estados para formulario de membresías
  const [showMembershipForm, setShowMembershipForm] = useState(false);
  const [editingMembership, setEditingMembership] = useState<Membership | null>(null);
  const [membershipFormData, setMembershipFormData] = useState<MembershipFormData>({
    name: '',
    price: 0,
    reservationsPerMonth: 8,
    imageUrl: '',
    isActive: true,
    timeRestriction: 'all_day',
    classRestriction: 'all',
    allowedClassTypeIds: [],
    weeklyLimitEnabled: false,
    weeklyClassCount: 2
  });
  const [uploadingMembershipImage, setUploadingMembershipImage] = useState(false);
  const [membershipImagePreview, setMembershipImagePreview] = useState<string | null>(null);
  const membershipFileInputRef = useRef<HTMLInputElement>(null);

  // Cargar datos
  const loadData = async () => {
    if (!businessId) {
      setLoading(false);
      return;
    }

    try {
      // Cargar configuración del negocio
      const businessDoc = await getDoc(doc(db, 'businesses', businessId));
      if (businessDoc.exists()) {
        const businessData = businessDoc.data();
        setPaymentMode(businessData.paymentMode || 'in_person');
      }

      // Cargar tipos de clase
      const classTypesQuery = query(collection(db, 'classTypes'), where('businessId', '==', businessId));
      const classTypesSnapshot = await getDocs(classTypesQuery);
      const classTypesList = classTypesSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        isActive: doc.data().isActive
      })) as ClassType[];
      setClassTypes(classTypesList);

      // Cargar bonos
      const passQuery = query(collection(db, 'passTypes'), where('businessId', '==', businessId));
      const passSnapshot = await getDocs(passQuery);
      const passes = passSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timeRestriction: doc.data().timeRestriction || 'all_day',
        classRestriction: doc.data().classRestriction || 'all',
        allowedClassTypeIds: doc.data().allowedClassTypeIds || [],
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as PassType[];
      passes.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setPassTypes(passes);

      // Cargar membresías
      const membershipQuery = query(collection(db, 'memberships'), where('businessId', '==', businessId));
      const membershipSnapshot = await getDocs(membershipQuery);
      const membershipList = membershipSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timeRestriction: doc.data().timeRestriction || 'all_day',
        classRestriction: doc.data().classRestriction || 'all',
        allowedClassTypeIds: doc.data().allowedClassTypeIds || [],
        weeklyLimitEnabled: doc.data().weeklyLimitEnabled || false,
        weeklyClassCount: doc.data().weeklyClassCount || 2,
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as Membership[];
      membershipList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setMemberships(membershipList);

    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Guardar modo de pago
  const handlePaymentModeChange = async (mode: 'online' | 'in_person') => {
    if (!businessId) return;

    setSavingPaymentMode(true);
    try {
      await updateDoc(doc(db, 'businesses', businessId), {
        paymentMode: mode,
        updatedAt: new Date()
      });
      setPaymentMode(mode);
      toast.success(mode === 'online'
        ? 'Pago online habilitado. Los usuarios podrán contratar desde la app.'
        : 'Pago en persona activado. Los usuarios deberán contactar contigo para contratar.'
      );
    } catch (error) {
      console.error('Error guardando modo de pago:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setSavingPaymentMode(false);
    }
  };

  useEffect(() => {
    if (businessId) {
      loadData();
      loadStripeStatus();
    }
  }, [businessId]);

  // Cargar estado de Stripe Connect
  const loadStripeStatus = async () => {
    if (!businessId) return;

    setLoadingStripe(true);
    try {
      const getConnectAccountStatus = httpsCallable(functions, 'getConnectAccountStatus');
      const result = await getConnectAccountStatus({ businessId });
      const data = result.data as StripeConnectStatus;
      setStripeStatus(data);
    } catch (error) {
      console.error('Error loading Stripe status:', error);
      setStripeStatus({ connected: false, status: 'not_created' });
    } finally {
      setLoadingStripe(false);
    }
  };

  // Detectar parámetros de URL para Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeParam = params.get('stripe');

    if (stripeParam === 'success') {
      toast.success('¡Cuenta de Stripe conectada correctamente!');
      window.history.replaceState({}, '', window.location.pathname);
      loadStripeStatus();
    } else if (stripeParam === 'refresh') {
      toast('Completa la verificación de tu cuenta', { icon: '⚠️' });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // ========== FUNCIONES PARA BONOS ==========
  const handlePassFormChange = (field: keyof PassFormData, value: string | number | boolean) => {
    setPassFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePassImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !businessId) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    setUploadingPassImage(true);

    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPassImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      const compressedFile = await compressImage(file);
      const fileName = `passes/${businessId}/${Date.now()}_${compressedFile.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, compressedFile);
      const downloadUrl = await getDownloadURL(storageRef);

      setPassFormData(prev => ({ ...prev, imageUrl: downloadUrl }));
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Error subiendo imagen:', error);
      toast.error('Error al subir la imagen');
    } finally {
      setUploadingPassImage(false);
    }
  };

  const validatePassForm = (): boolean => {
    if (!passFormData.passName.trim()) {
      toast.error('El nombre del bono es requerido');
      return false;
    }
    if (passFormData.classCredits < 1) {
      toast.error('El número de reservas debe ser al menos 1');
      return false;
    }
    if (passFormData.validityInDays < 1) {
      toast.error('La validez debe ser al menos 1 día');
      return false;
    }
    return true;
  };

  const handleCreatePass = async () => {
    if (!businessId || !validatePassForm()) return;

    try {
      const newPass = {
        passName: passFormData.passName.trim(),
        classCredits: passFormData.classCredits,
        validityInDays: passFormData.validityInDays,
        price: passFormData.price || 0,
        imageUrl: passFormData.imageUrl || '',
        isActive: passFormData.isActive,
        timeRestriction: passFormData.timeRestriction,
        classRestriction: passFormData.classRestriction,
        allowedClassTypeIds: passFormData.classRestriction === 'specific' ? passFormData.allowedClassTypeIds : [],
        businessId,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'passTypes'), newPass);
      toast.success('Bono creado exitosamente');
      resetPassForm();
      loadData();
    } catch (error) {
      console.error('Error creando bono:', error);
      toast.error('Error al crear el bono');
    }
  };

  const handleUpdatePass = async () => {
    if (!editingPass || !validatePassForm()) return;

    try {
      await updateDoc(doc(db, 'passTypes', editingPass.id), {
        passName: passFormData.passName.trim(),
        classCredits: passFormData.classCredits,
        validityInDays: passFormData.validityInDays,
        price: passFormData.price || 0,
        imageUrl: passFormData.imageUrl || '',
        isActive: passFormData.isActive,
        timeRestriction: passFormData.timeRestriction,
        classRestriction: passFormData.classRestriction,
        allowedClassTypeIds: passFormData.classRestriction === 'specific' ? passFormData.allowedClassTypeIds : [],
        updatedAt: new Date()
      });
      toast.success('Bono actualizado exitosamente');
      resetPassForm();
      loadData();
    } catch (error) {
      console.error('Error actualizando bono:', error);
      toast.error('Error al actualizar el bono');
    }
  };

  const handleDeletePass = async (passId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este bono?')) return;

    try {
      await deleteDoc(doc(db, 'passTypes', passId));
      toast.success('Bono eliminado exitosamente');
      loadData();
    } catch (error) {
      console.error('Error eliminando bono:', error);
      toast.error('Error al eliminar el bono');
    }
  };

  const handleEditPass = (pass: PassType) => {
    setEditingPass(pass);
    setPassFormData({
      passName: pass.passName,
      classCredits: pass.classCredits,
      validityInDays: pass.validityInDays,
      price: pass.price || 0,
      imageUrl: pass.imageUrl || '',
      isActive: pass.isActive,
      timeRestriction: pass.timeRestriction || 'all_day',
      classRestriction: pass.classRestriction || 'all',
      allowedClassTypeIds: pass.allowedClassTypeIds || []
    });
    setPassImagePreview(pass.imageUrl || null);
    setShowPassForm(true);
    setShowMembershipForm(false);
  };

  const resetPassForm = () => {
    setPassFormData({
      passName: '',
      classCredits: 1,
      validityInDays: 30,
      price: 0,
      imageUrl: '',
      isActive: true,
      timeRestriction: 'all_day',
      classRestriction: 'all',
      allowedClassTypeIds: []
    });
    setEditingPass(null);
    setShowPassForm(false);
    setPassImagePreview(null);
  };

  const togglePassStatus = async (pass: PassType) => {
    try {
      await updateDoc(doc(db, 'passTypes', pass.id), {
        isActive: !pass.isActive,
        updatedAt: new Date()
      });
      toast.success(`Bono ${!pass.isActive ? 'activado' : 'desactivado'}`);
      loadData();
    } catch (error) {
      console.error('Error cambiando estado:', error);
      toast.error('Error al cambiar el estado');
    }
  };

  // ========== FUNCIONES PARA MEMBRESÍAS ==========
  const handleMembershipFormChange = (field: keyof MembershipFormData, value: string | number | boolean) => {
    setMembershipFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMembershipImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !businessId) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    setUploadingMembershipImage(true);

    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setMembershipImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      const compressedFile = await compressImage(file);
      const fileName = `memberships/${businessId}/${Date.now()}_${compressedFile.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, compressedFile);
      const downloadUrl = await getDownloadURL(storageRef);

      setMembershipFormData(prev => ({ ...prev, imageUrl: downloadUrl }));
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Error subiendo imagen:', error);
      toast.error('Error al subir la imagen');
    } finally {
      setUploadingMembershipImage(false);
    }
  };

  const validateMembershipForm = (): boolean => {
    if (!membershipFormData.name.trim()) {
      toast.error('El nombre de la membresía es requerido');
      return false;
    }
    if (membershipFormData.price < 0) {
      toast.error('El precio no puede ser negativo');
      return false;
    }
    if (membershipFormData.reservationsPerMonth < 1) {
      toast.error('Las reservas por mes deben ser al menos 1');
      return false;
    }
    return true;
  };

  const handleCreateMembership = async () => {
    if (!businessId || !validateMembershipForm()) return;

    try {
      const newMembership = {
        name: membershipFormData.name.trim(),
        price: membershipFormData.price,
        reservationsPerMonth: membershipFormData.reservationsPerMonth,
        imageUrl: membershipFormData.imageUrl || '',
        isActive: membershipFormData.isActive,
        timeRestriction: membershipFormData.timeRestriction,
        classRestriction: membershipFormData.classRestriction,
        allowedClassTypeIds: membershipFormData.classRestriction === 'specific' ? membershipFormData.allowedClassTypeIds : [],
        weeklyLimitEnabled: membershipFormData.weeklyLimitEnabled,
        weeklyClassCount: membershipFormData.weeklyLimitEnabled ? membershipFormData.weeklyClassCount : 0,
        businessId,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'memberships'), newMembership);
      toast.success('Membresía creada exitosamente');
      resetMembershipForm();
      loadData();
    } catch (error) {
      console.error('Error creando membresía:', error);
      toast.error('Error al crear la membresía');
    }
  };

  const handleUpdateMembership = async () => {
    if (!editingMembership || !validateMembershipForm()) return;

    try {
      await updateDoc(doc(db, 'memberships', editingMembership.id), {
        name: membershipFormData.name.trim(),
        price: membershipFormData.price,
        reservationsPerMonth: membershipFormData.reservationsPerMonth,
        imageUrl: membershipFormData.imageUrl || '',
        isActive: membershipFormData.isActive,
        timeRestriction: membershipFormData.timeRestriction,
        classRestriction: membershipFormData.classRestriction,
        allowedClassTypeIds: membershipFormData.classRestriction === 'specific' ? membershipFormData.allowedClassTypeIds : [],
        weeklyLimitEnabled: membershipFormData.weeklyLimitEnabled,
        weeklyClassCount: membershipFormData.weeklyLimitEnabled ? membershipFormData.weeklyClassCount : 0,
        updatedAt: new Date()
      });
      toast.success('Membresía actualizada exitosamente');
      resetMembershipForm();
      loadData();
    } catch (error) {
      console.error('Error actualizando membresía:', error);
      toast.error('Error al actualizar la membresía');
    }
  };

  const handleDeleteMembership = async (membershipId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta membresía?')) return;

    try {
      await deleteDoc(doc(db, 'memberships', membershipId));
      toast.success('Membresía eliminada exitosamente');
      loadData();
    } catch (error) {
      console.error('Error eliminando membresía:', error);
      toast.error('Error al eliminar la membresía');
    }
  };

  const handleEditMembership = (membership: Membership) => {
    setEditingMembership(membership);
    setMembershipFormData({
      name: membership.name,
      price: membership.price,
      reservationsPerMonth: membership.reservationsPerMonth,
      imageUrl: membership.imageUrl || '',
      isActive: membership.isActive,
      timeRestriction: membership.timeRestriction || 'all_day',
      classRestriction: membership.classRestriction || 'all',
      allowedClassTypeIds: membership.allowedClassTypeIds || [],
      weeklyLimitEnabled: membership.weeklyLimitEnabled || false,
      weeklyClassCount: membership.weeklyClassCount || 2
    });
    setMembershipImagePreview(membership.imageUrl || null);
    setShowMembershipForm(true);
    setShowPassForm(false);
  };

  const resetMembershipForm = () => {
    setMembershipFormData({
      name: '',
      price: 0,
      reservationsPerMonth: 8,
      imageUrl: '',
      isActive: true,
      timeRestriction: 'all_day',
      classRestriction: 'all',
      allowedClassTypeIds: [],
      weeklyLimitEnabled: false,
      weeklyClassCount: 2
    });
    setEditingMembership(null);
    setShowMembershipForm(false);
    setMembershipImagePreview(null);
  };

  // Helper para toggle de clases permitidas
  const toggleAllowedClassType = (classTypeId: string, formType: 'membership' | 'pass') => {
    if (formType === 'membership') {
      const current = membershipFormData.allowedClassTypeIds;
      const updated = current.includes(classTypeId)
        ? current.filter(id => id !== classTypeId)
        : [...current, classTypeId];
      setMembershipFormData(prev => ({ ...prev, allowedClassTypeIds: updated }));
    } else {
      const current = passFormData.allowedClassTypeIds;
      const updated = current.includes(classTypeId)
        ? current.filter(id => id !== classTypeId)
        : [...current, classTypeId];
      setPassFormData(prev => ({ ...prev, allowedClassTypeIds: updated }));
    }
  };

  const toggleMembershipStatus = async (membership: Membership) => {
    try {
      await updateDoc(doc(db, 'memberships', membership.id), {
        isActive: !membership.isActive,
        updatedAt: new Date()
      });
      toast.success(`Membresía ${!membership.isActive ? 'activada' : 'desactivada'}`);
      loadData();
    } catch (error) {
      console.error('Error cambiando estado:', error);
      toast.error('Error al cambiar el estado');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loading size="lg" className="mx-auto mb-4" />
          <p className="text-gray-400">Cargando membresías y bonos...</p>
        </div>
      </div>
    );
  }

  if (!businessId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">No hay negocio seleccionado</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Membresías y Bonos</title>
      </Helmet>

      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-amber-400">
            Membresías y Bonos
          </h1>
          <p className="text-gray-400 mt-2">
            Administra las membresías mensuales y bonos de clases. Los usuarios pueden tener ambos activos simultáneamente.
          </p>
        </div>

        {/* Configuración de Modo de Pago */}
        <Card className="p-6 bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-amber-400" />
                Modo de Cobro
              </h3>
              <p className="text-gray-400 text-sm mt-1">
                Define cómo los usuarios pueden contratar membresías y bonos
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => handlePaymentModeChange('in_person')}
                disabled={savingPaymentMode}
                className={`px-4 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${paymentMode === 'in_person'
                  ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/25'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
              >
                <Users className="w-4 h-4" />
                En Persona
              </button>
              <button
                onClick={() => handlePaymentModeChange('online')}
                disabled={savingPaymentMode || !stripeStatus.connected}
                className={`px-4 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${paymentMode === 'online'
                  ? 'bg-green-500 text-white shadow-lg shadow-green-500/25'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  } ${!stripeStatus.connected ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <CreditCard className="w-4 h-4" />
                Online
              </button>
            </div>
          </div>

          {/* Estado de Stripe Connect - Simplificado */}
          <div className={`mt-6 p-4 rounded-xl border ${stripeStatus.connected
            ? 'bg-green-900/20 border-green-700/50'
            : 'bg-amber-900/20 border-amber-700/50'
            }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${stripeStatus.connected ? 'bg-green-500/20' : 'bg-amber-500/20'
                  }`}>
                  {loadingStripe ? (
                    <Loading size="sm" />
                  ) : stripeStatus.connected ? (
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-amber-400" />
                  )}
                </div>
                <div>
                  <p className="text-white font-medium">
                    {stripeStatus.connected ? 'Stripe Conectado ✓' : 'Stripe no conectado'}
                  </p>
                  <p className="text-gray-400 text-sm">
                    {stripeStatus.connected
                      ? 'Puedes recibir pagos online de tus clientes'
                      : 'Configura Stripe en la sección de Cobros'}
                  </p>
                </div>
              </div>

              {!stripeStatus.connected && (
                <a
                  href="/admin/payments"
                  className="px-4 py-2 bg-[#635BFF] hover:bg-[#5851DB] text-white rounded-lg font-medium text-sm flex items-center gap-2 transition-colors"
                >
                  <CreditCard className="w-4 h-4" />
                  Ir a Cobros
                </a>
              )}
            </div>
          </div>

          {/* Descripción del modo seleccionado */}
          <div className={`mt-4 p-4 rounded-xl ${paymentMode === 'online'
            ? 'bg-green-900/20 border border-green-700/50'
            : 'bg-amber-900/20 border border-amber-700/50'
            }`}>
            {paymentMode === 'online' ? (
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <CreditCard className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-green-400 font-medium">Pago Online Habilitado</p>
                  <p className="text-green-400/70 text-sm mt-1">
                    Los usuarios pueden contratar membresías y bonos directamente desde la aplicación.
                    <span className="block mt-1 text-xs opacity-75">
                      💳 Los pagos se depositan en tu cuenta de Stripe automáticamente.
                    </span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Users className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-amber-400 font-medium">Pago en Persona</p>
                  <p className="text-amber-400/70 text-sm mt-1">
                    Los usuarios verán las opciones disponibles pero deberán contactar contigo para contratar.
                    <span className="block mt-1 text-xs opacity-75">
                      📍 Asigna membresías y bonos manualmente desde la sección de Usuarios.
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Formulario de Membresía */}
        {showMembershipForm && (
          <Card className="p-6 bg-gray-900 border-gray-800">
            <h3 className="text-lg font-semibold text-amber-400 mb-4">
              {editingMembership ? 'Editar Membresía' : 'Nueva Membresía'}
            </h3>
            <p className="text-sm text-gray-400 mb-6">
              Las membresías se renuevan automáticamente cada día 1 de mes, reiniciando las reservas disponibles.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Imagen */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Imagen de la Membresía
                </label>
                <div className="flex items-start gap-4">
                  {(membershipImagePreview || membershipFormData.imageUrl) && (
                    <div className="relative w-32 h-32 rounded-xl overflow-hidden border border-gray-700">
                      <img
                        src={membershipImagePreview || membershipFormData.imageUrl}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      ref={membershipFileInputRef}
                      onChange={handleMembershipImageUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => membershipFileInputRef.current?.click()}
                      disabled={uploadingMembershipImage}
                      className="flex items-center gap-2"
                    >
                      {uploadingMembershipImage ? (
                        <>
                          <Loading size="sm" />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          {membershipImagePreview ? 'Cambiar imagen' : 'Subir imagen'}
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 mt-2">
                      Formatos: JPG, PNG, GIF.
                    </p>
                  </div>
                </div>
              </div>

              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nombre de la Membresía *
                </label>
                <Input
                  value={membershipFormData.name}
                  onChange={(e) => handleMembershipFormChange('name', e.target.value)}
                  placeholder="Ej: Membresía Mensual Premium"
                  required
                />
              </div>

              {/* Precio */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Precio Mensual (€) *
                </label>
                <Input
                  type="number"
                  value={membershipFormData.price}
                  onChange={(e) => handleMembershipFormChange('price', parseFloat(e.target.value) || 0)}
                  min={0}
                  step="0.01"
                  required
                />
              </div>

              {/* Reservas por mes */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Reservas permitidas por mes *
                </label>
                <Input
                  type="number"
                  value={membershipFormData.reservationsPerMonth}
                  onChange={(e) => handleMembershipFormChange('reservationsPerMonth', parseInt(e.target.value) || 1)}
                  min={1}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Se reinicia cada día 1 de mes
                </p>
              </div>

              {/* Límite Semanal */}
              <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-medium text-gray-300">
                    Límite de clases por semana
                  </label>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${membershipFormData.weeklyLimitEnabled ? 'text-amber-400' : 'text-gray-500'}`}>
                      {membershipFormData.weeklyLimitEnabled ? 'Activado' : 'Desactivado'}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleMembershipFormChange('weeklyLimitEnabled', !membershipFormData.weeklyLimitEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${membershipFormData.weeklyLimitEnabled ? 'bg-amber-600' : 'bg-gray-600'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${membershipFormData.weeklyLimitEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                </div>

                {membershipFormData.weeklyLimitEnabled && (
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">
                      Max. clases por semana (Lunes - Domingo)
                    </label>
                    <Input
                      type="number"
                      value={membershipFormData.weeklyClassCount}
                      onChange={(e) => handleMembershipFormChange('weeklyClassCount', parseInt(e.target.value) || 1)}
                      min={1}
                      className="bg-gray-900 border-gray-600 focus:border-amber-500"
                    />
                    <p className="text-xs text-amber-500/80 mt-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Si el usuario no consume estas clases en la semana, las pierde.
                    </p>
                  </div>
                )}
              </div>

              {/* Estado activo */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Estado
                </label>
                <button
                  type="button"
                  onClick={() => handleMembershipFormChange('isActive', !membershipFormData.isActive)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${membershipFormData.isActive
                    ? 'bg-green-600/20 text-green-400 border border-green-600'
                    : 'bg-gray-700/50 text-gray-400 border border-gray-600'
                    }`}
                >
                  {membershipFormData.isActive ? (
                    <>
                      <ToggleRight className="w-5 h-5" />
                      Activa
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="w-5 h-5" />
                      Inactiva
                    </>
                  )}
                </button>
              </div>

              {/* Restricción horaria */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Horario permitido
                </label>
                <select
                  value={membershipFormData.timeRestriction}
                  onChange={(e) => handleMembershipFormChange('timeRestriction', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  <option value="all_day">Todo el día</option>
                  <option value="morning">Solo mañanas (hasta 14:00)</option>
                  <option value="afternoon">Solo tardes (desde 14:00)</option>
                </select>
              </div>

              {/* Restricción de clases */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Clases permitidas
                </label>
                <div className="flex gap-4 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="membershipClassRestriction"
                      checked={membershipFormData.classRestriction === 'all'}
                      onChange={() => handleMembershipFormChange('classRestriction', 'all')}
                      className="w-4 h-4 text-amber-500 bg-gray-800 border-gray-600"
                    />
                    <span className="text-gray-300">Todas las clases</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="membershipClassRestriction"
                      checked={membershipFormData.classRestriction === 'specific'}
                      onChange={() => handleMembershipFormChange('classRestriction', 'specific')}
                      className="w-4 h-4 text-amber-500 bg-gray-800 border-gray-600"
                    />
                    <span className="text-gray-300">Solo clases específicas</span>
                  </label>
                </div>

                {membershipFormData.classRestriction === 'specific' && (
                  <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <p className="text-sm text-gray-400 mb-3">Selecciona las clases permitidas:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {classTypes.filter(ct => ct.isActive).map(classType => (
                        <label
                          key={classType.id}
                          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${membershipFormData.allowedClassTypeIds.includes(classType.id)
                            ? 'bg-amber-600/20 border border-amber-500'
                            : 'bg-gray-700/50 border border-gray-600 hover:border-gray-500'
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={membershipFormData.allowedClassTypeIds.includes(classType.id)}
                            onChange={() => toggleAllowedClassType(classType.id, 'membership')}
                            className="w-4 h-4 text-amber-500 bg-gray-800 border-gray-600 rounded"
                          />
                          <span className="text-sm text-white">{classType.name}</span>
                        </label>
                      ))}
                    </div>
                    {classTypes.filter(ct => ct.isActive).length === 0 && (
                      <p className="text-gray-500 text-sm">No hay tipos de clase activos</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={editingMembership ? handleUpdateMembership : handleCreateMembership}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
              >
                {editingMembership ? 'Actualizar' : 'Crear'} Membresía
              </Button>
              <Button
                variant="outline"
                onClick={resetMembershipForm}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </Card>
        )}

        {/* Formulario de Bono */}
        {showPassForm && (
          <Card className="p-6 bg-gray-900 border-gray-800">
            <h3 className="text-lg font-semibold text-primary-400 mb-4">
              {editingPass ? 'Editar Bono' : 'Nuevo Bono de Clases'}
            </h3>
            <p className="text-sm text-gray-400 mb-6">
              Los bonos tienen una duración determinada desde el día de contratación. No se renuevan automáticamente.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Imagen */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Imagen del Bono
                </label>
                <div className="flex items-start gap-4">
                  {(passImagePreview || passFormData.imageUrl) && (
                    <div className="relative w-32 h-32 rounded-xl overflow-hidden border border-gray-700">
                      <img
                        src={passImagePreview || passFormData.imageUrl}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      ref={passFileInputRef}
                      onChange={handlePassImageUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => passFileInputRef.current?.click()}
                      disabled={uploadingPassImage}
                      className="flex items-center gap-2"
                    >
                      {uploadingPassImage ? (
                        <>
                          <Loading size="sm" />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          {passImagePreview ? 'Cambiar imagen' : 'Subir imagen'}
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 mt-2">
                      Formatos: JPG, PNG, GIF.
                    </p>
                  </div>
                </div>
              </div>

              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nombre del Bono *
                </label>
                <Input
                  value={passFormData.passName}
                  onChange={(e) => handlePassFormChange('passName', e.target.value)}
                  placeholder="Ej: Bono 10 Clases"
                  required
                />
              </div>

              {/* Precio */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Precio (€) *
                </label>
                <Input
                  type="number"
                  value={passFormData.price}
                  onChange={(e) => handlePassFormChange('price', parseFloat(e.target.value) || 0)}
                  min={0}
                  step="0.01"
                  required
                />
              </div>

              {/* Número de reservas */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Cantidad de Reservas *
                </label>
                <Input
                  type="number"
                  value={passFormData.classCredits}
                  onChange={(e) => handlePassFormChange('classCredits', parseInt(e.target.value) || 1)}
                  min={1}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Número de clases que puede reservar
                </p>
              </div>

              {/* Validez en días */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Duración (días) *
                </label>
                <Input
                  type="number"
                  value={passFormData.validityInDays}
                  onChange={(e) => handlePassFormChange('validityInDays', parseInt(e.target.value) || 1)}
                  min={1}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Días desde la contratación hasta que expira
                </p>
              </div>

              {/* Estado activo */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Estado
                </label>
                <button
                  type="button"
                  onClick={() => handlePassFormChange('isActive', !passFormData.isActive)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${passFormData.isActive
                    ? 'bg-green-600/20 text-green-400 border border-green-600'
                    : 'bg-gray-700/50 text-gray-400 border border-gray-600'
                    }`}
                >
                  {passFormData.isActive ? (
                    <>
                      <ToggleRight className="w-5 h-5" />
                      Activo
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="w-5 h-5" />
                      Inactivo
                    </>
                  )}
                </button>
              </div>

              {/* Restricción horaria */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Horario permitido
                </label>
                <select
                  value={passFormData.timeRestriction}
                  onChange={(e) => handlePassFormChange('timeRestriction', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all_day">Todo el día</option>
                  <option value="morning">Solo mañanas (hasta 14:00)</option>
                  <option value="afternoon">Solo tardes (desde 14:00)</option>
                </select>
              </div>

              {/* Restricción de clases */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Clases permitidas
                </label>
                <div className="flex gap-4 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="passClassRestriction"
                      checked={passFormData.classRestriction === 'all'}
                      onChange={() => handlePassFormChange('classRestriction', 'all')}
                      className="w-4 h-4 text-primary-500 bg-gray-800 border-gray-600"
                    />
                    <span className="text-gray-300">Todas las clases</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="passClassRestriction"
                      checked={passFormData.classRestriction === 'specific'}
                      onChange={() => handlePassFormChange('classRestriction', 'specific')}
                      className="w-4 h-4 text-primary-500 bg-gray-800 border-gray-600"
                    />
                    <span className="text-gray-300">Solo clases específicas</span>
                  </label>
                </div>

                {passFormData.classRestriction === 'specific' && (
                  <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <p className="text-sm text-gray-400 mb-3">Selecciona las clases permitidas:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {classTypes.filter(ct => ct.isActive).map(classType => (
                        <label
                          key={classType.id}
                          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${passFormData.allowedClassTypeIds.includes(classType.id)
                            ? 'bg-primary-600/20 border border-primary-500'
                            : 'bg-gray-700/50 border border-gray-600 hover:border-gray-500'
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={passFormData.allowedClassTypeIds.includes(classType.id)}
                            onChange={() => toggleAllowedClassType(classType.id, 'pass')}
                            className="w-4 h-4 text-primary-500 bg-gray-800 border-gray-600 rounded"
                          />
                          <span className="text-sm text-white">{classType.name}</span>
                        </label>
                      ))}
                    </div>
                    {classTypes.filter(ct => ct.isActive).length === 0 && (
                      <p className="text-gray-500 text-sm">No hay tipos de clase activos</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={editingPass ? handleUpdatePass : handleCreatePass}
                className="flex-1"
              >
                {editingPass ? 'Actualizar' : 'Crear'} Bono
              </Button>
              <Button
                variant="outline"
                onClick={resetPassForm}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </Card>
        )}

        {/* Sección de Membresías */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-amber-400 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Membresías Mensuales
            </h2>
            {!showMembershipForm && (
              <Button
                onClick={() => { setShowMembershipForm(true); setShowPassForm(false); }}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Añadir
              </Button>
            )}
          </div>

          {memberships.length === 0 && !showMembershipForm ? (
            <Card className="p-8 text-center bg-gray-900 border-gray-800">
              <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                No hay membresías
              </h3>
              <p className="text-gray-400 mb-4">
                Crea tu primera membresía mensual
              </p>
              <Button onClick={() => { setShowMembershipForm(true); setShowPassForm(false); }} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-2" />
                Crear Membresía
              </Button>
            </Card>
          ) : memberships.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {memberships.map((membership) => (
                <Card key={membership.id} className="overflow-hidden bg-gray-900 border-gray-800">
                  {/* Imagen */}
                  {membership.imageUrl ? (
                    <div className="h-40 overflow-hidden">
                      <img
                        src={membership.imageUrl}
                        alt={membership.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-40 bg-gradient-to-br from-amber-600 to-orange-500 flex items-center justify-center">
                      <CreditCard className="w-16 h-16 text-white/50" />
                    </div>
                  )}

                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-white text-lg">
                          {membership.name}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {membership.reservationsPerMonth} reservas/mes
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditMembership(membership)}
                          className="p-1 text-gray-400 hover:text-amber-400 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteMembership(membership.id)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="text-2xl font-bold text-amber-400 mb-4">
                      {membership.price.toFixed(2)}€<span className="text-sm font-normal text-gray-400">/mes</span>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                      <span className="text-gray-400 text-sm">Estado:</span>
                      <button
                        onClick={() => toggleMembershipStatus(membership)}
                        className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${membership.isActive
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                          }`}
                      >
                        {membership.isActive ? (
                          <>
                            <ToggleRight className="w-4 h-4" />
                            Activa
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="w-4 h-4" />
                            Inactiva
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Sección de Bonos */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-primary-400 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Bonos de Clases
            </h2>
            {!showPassForm && (
              <Button
                onClick={() => { setShowPassForm(true); setShowMembershipForm(false); }}
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Añadir
              </Button>
            )}
          </div>

          {passTypes.length === 0 && !showPassForm ? (
            <Card className="p-8 text-center bg-gray-900 border-gray-800">
              <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                No hay bonos
              </h3>
              <p className="text-gray-400 mb-4">
                Crea tu primer bono de clases
              </p>
              <Button onClick={() => { setShowPassForm(true); setShowMembershipForm(false); }}>
                <Plus className="w-4 h-4 mr-2" />
                Crear Bono
              </Button>
            </Card>
          ) : passTypes.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {passTypes.map((pass) => (
                <Card key={pass.id} className="overflow-hidden bg-gray-900 border-gray-800">
                  {/* Imagen */}
                  {pass.imageUrl ? (
                    <div className="h-40 overflow-hidden">
                      <img
                        src={pass.imageUrl}
                        alt={pass.passName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-40 bg-gradient-to-br from-primary-600 to-purple-500 flex items-center justify-center">
                      <Package className="w-16 h-16 text-white/50" />
                    </div>
                  )}

                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-white text-lg">
                          {pass.passName}
                        </h3>
                        <div className="flex flex-col gap-1 text-sm text-gray-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {pass.classCredits} reservas
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Válido {pass.validityInDays} días
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditPass(pass)}
                          className="p-1 text-gray-400 hover:text-primary-400 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePass(pass.id)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="text-2xl font-bold text-primary-400 mb-4">
                      {(pass.price || 0).toFixed(2)}€
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                      <span className="text-gray-400 text-sm">Estado:</span>
                      <button
                        onClick={() => togglePassStatus(pass)}
                        className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${pass.isActive
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                          }`}
                      >
                        {pass.isActive ? (
                          <>
                            <ToggleRight className="w-4 h-4" />
                            Activo
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="w-4 h-4" />
                            Inactivo
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Nota informativa */}
        <Card className="p-6 bg-gray-900/50 border-gray-800">
          <h3 className="text-white font-semibold mb-2">💡 Información importante</h3>
          <ul className="text-gray-400 text-sm space-y-1">
            <li>• <strong className="text-amber-400">Membresías:</strong> Se renuevan automáticamente cada día 1 de mes, reiniciando las reservas.</li>
            <li>• <strong className="text-primary-400">Bonos:</strong> Tienen una duración fija desde el día de contratación. No se renuevan.</li>
            <li>• Los usuarios pueden tener <strong className="text-white">ambos activos simultáneamente</strong>.</li>
          </ul>
        </Card>
      </div>
    </>
  );
};

export default ClassPassManagementPage;
